import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { roomsService, propertiesService, imageService } from '../services/api';
import { useIsMobile } from '../hooks';
import { Image as ImageIcon } from 'lucide-react';

// Professional icon components (using Unicode symbols for formal appearance)
const Icons = {
  room: '⌂',
  bed: '',
  add: '+',
  edit: '',
  delete: '✕',
  settings: '',
  search: '⌕',
  refresh: '↻',
  check: '✓',
  close: '✕',
  loading: '◐',
  private: '●',
  shared: '◐',
  available: '●',
  occupied: '●',
  cleaning: '●',
  blocked: '●',
  female: '',
  male: '',
  currency: '$',
  // Enhanced bed configuration icons
  bedAvailable: '▣',
  bedUnavailable: '▢',
  bedMaintenance: '',
  toggle: '⇄',
  status: '◉',
  configure: '',
  power: '⏻',
  lock: '',
  unlock: '',
  visibility: '',
  hidden: '',
  // Image management icons
  image: '',
  camera: '',
  upload: '',
  gallery: '',
  photo: '',
  picture: ''
};

// Professional color palette
const Colors = {
  primary: '#24332A',
  secondary: '#36433B',
  accent: '#1A3E2A',
  success: '#163626',
  warning: '#9E712E',
  danger: '#8E3628',
  info: '#1A3E2A',
  light: '#F5F3EB',
  border: '#E3DFD3',
  muted: '#5F6B61',
  white: '#ffffff',
  available: '#163626',
  occupied: '#8E3628',
  cleaning: '#9E712E',
  blocked: '#5F6B61'
};

function RoomManagement({ onRoomUpdate }) {
  const isMobile = useIsMobile();
  const [rooms, setRooms] = useState([]);
  const [properties, setProperties] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddRoomModal, setShowAddRoomModal] = useState(false);
  const [showEditRoomModal, setShowEditRoomModal] = useState(false);
  const [showAddBedModal, setShowAddBedModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedBed, setSelectedBed] = useState(null);
  const [expandedRoom, setExpandedRoom] = useState(null);
  
  // Image management states
  const [roomImages, setRoomImages] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [imageDescription, setImageDescription] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedImageForView, setSelectedImageForView] = useState(null);
  
  // Form states
  const [roomForm, setRoomForm] = useState({
    roomNumber: '',
    roomTypeId: '',
    propertyId: '',
    status: 'AVAILABLE'
  });
  
  const [bedForm, setBedForm] = useState({
    bedNumber: '',
    isAvailable: true,
    isFemaleOnly: false
  });

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [roomsResponse, propertiesResponse, roomTypesResponse] = await Promise.all([
        roomsService.getAll({ limit: 100 }),
        propertiesService.getAll({ limit: 100 }),
        roomsService.getRoomTypes()
      ]);

      const roomsData = roomsResponse.data?.rooms || roomsResponse.rooms || [];
      setRooms(roomsData);
      setProperties(propertiesResponse.data?.properties || propertiesResponse.properties || []);
      setRoomTypes(roomTypesResponse.data?.roomTypes || roomTypesResponse.roomTypes || []);

      // Load images for all rooms
      await loadAllRoomImages(roomsData);
      
    } catch (error) {
      toast.error('Failed to load room data');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load images for all rooms
  const loadAllRoomImages = async (roomsData) => {
    try {
      const imagePromises = roomsData.map(async (room) => {
        try {
          const imagesResponse = await imageService.getImages('room', room.id);
          return { roomId: room.id, images: imagesResponse.data || [] };
        } catch (error) {
          console.error(`Failed to load images for room ${room.id}:`, error);
          return { roomId: room.id, images: [] };
        }
      });

      const imageResults = await Promise.all(imagePromises);
      const imageMap = {};
      imageResults.forEach(({ roomId, images }) => {
        imageMap[roomId] = images;
      });
      setRoomImages(imageMap);
    } catch (error) {
      console.error('Error loading room images:', error);
    }
  };

  // Load images for a specific room
  const loadRoomImages = async (roomId) => {
    try {
      console.log(`Loading images for room ${roomId}`);
      const imagesResponse = await imageService.getImages('room', roomId);
      console.log(`Room Images Response for room ${roomId}:`, imagesResponse);
      
      const images = imagesResponse.data || [];
      console.log(`Found ${images.length} room images:`, images);
      
      // Log each image URL for debugging
      images.forEach((image, index) => {
        console.log(`Room ${roomId} Image ${index + 1}:`, {
          id: image.id,
          url: image.url,
          description: image.description,
          createdAt: image.createdAt
        });
        
        // Test if the URL is accessible
        fetch(image.url, { method: 'HEAD' })
          .then(response => {
            console.log(`Room image ${image.id} URL test:`, {
              url: image.url,
              status: response.status,
              ok: response.ok,
              contentType: response.headers.get('content-type')
            });
          })
          .catch(error => {
            console.error(`Room image ${image.id} URL test failed:`, {
              url: image.url,
              error: error.message
            });
          });
      });
      
      setRoomImages(prev => ({
        ...prev,
        [roomId]: images
      }));
    } catch (error) {
      console.error(`Error loading images for room ${roomId}:`, error);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
    
    // Create preview for single image
    if (files.length === 1) {
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(files[0]);
    } else {
      setImagePreview(null);
    }
  };

  // Upload images for room
  const handleImageUpload = async () => {
    if (!selectedRoom || selectedFiles.length === 0) {
      toast.error('Please select a room and at least one image');
      return;
    }

    setUploadingImages(true);
    try {
      const formData = new FormData();
      
      if (selectedFiles.length === 1) {
        // Single image upload
        formData.append('image', selectedFiles[0]);
      } else {
        // Multiple image upload
        selectedFiles.forEach(file => {
          formData.append('images', file);
        });
      }
      
      formData.append('roomId', selectedRoom.id);
      if (imageDescription.trim()) {
        formData.append('description', imageDescription.trim());
      }

      if (selectedFiles.length === 1) {
        await imageService.uploadSingle(formData);
        toast.success('Image uploaded successfully');
      } else {
        await imageService.uploadMultiple(formData);
        toast.success(`${selectedFiles.length} images uploaded successfully`);
      }

      // Reload images for this room
      await loadRoomImages(selectedRoom.id);
      
      // Reset form
      setSelectedFiles([]);
      setImageDescription('');
      setImagePreview(null);
      setShowImageModal(false);
      
    } catch (error) {
      toast.error('Failed to upload images: ' + (error.message || 'Unknown error'));
    } finally {
      setUploadingImages(false);
    }
  };

  // Delete image
  const handleDeleteImage = async (imageId, roomId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      await imageService.deleteImage(imageId);
      toast.success('Image deleted successfully');
      await loadRoomImages(roomId);
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  // Update image description
  const handleUpdateImageDescription = async (imageId, newDescription, roomId) => {
    try {
      await imageService.updateImage(imageId, newDescription);
      toast.success('Image description updated');
      await loadRoomImages(roomId);
    } catch (error) {
      toast.error('Failed to update image description');
    }
  };

  // Open image management modal
  const openImageModal = (room) => {
    setSelectedRoom(room);
    setSelectedFiles([]);
    setImageDescription('');
    setImagePreview(null);
    setShowImageModal(true);
  };

  // Handle image click for large view
  const handleImageClick = (image) => {
    console.log('Room image clicked for large view:', {
      id: image.id,
      url: image.url,
      description: image.description
    });
    
    // Test the image URL before showing modal
    const img = new Image();
    img.onload = () => {
      console.log('Room image loaded successfully in test:', image.url);
    };
    img.onerror = (error) => {
      console.error('Room image failed to load in test:', {
        url: image.url,
        error: error
      });
    };
    img.src = image.url;
    
    setSelectedImageForView(image);
  };

  // Handle image view close
  const handleImageViewClose = () => {
    setSelectedImageForView(null);
  };

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    if (!roomForm.roomNumber || !roomForm.roomTypeId || !roomForm.propertyId) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await roomsService.create(roomForm);
      toast.success('Room created successfully');
      setShowAddRoomModal(false);
      resetRoomForm();
      loadData();
      onRoomUpdate?.();
    } catch (error) {
      toast.error('Failed to create room: ' + (error.message || 'Unknown error'));
    }
  };

  const handleUpdateRoom = async (e) => {
    e.preventDefault();
    if (!selectedRoom || !roomForm.roomNumber || !roomForm.roomTypeId || !roomForm.propertyId) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await roomsService.update(selectedRoom.id, roomForm);
      toast.success('Room updated successfully');
      setShowEditRoomModal(false);
      setSelectedRoom(null);
      resetRoomForm();
      loadData();
      onRoomUpdate?.();
    } catch (error) {
      toast.error('Failed to update room: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDeleteRoom = async (room) => {
    if (!window.confirm(`Are you sure you want to delete Room ${room.roomNumber}? This will also delete all beds in this room.`)) {
      return;
    }

    try {
      await roomsService.delete(room.id);
      toast.success('Room deleted successfully');
      loadData();
      onRoomUpdate?.();
    } catch (error) {
      toast.error('Failed to delete room: ' + (error.message || 'Unknown error'));
    }
  };

  const handleAddBed = async (e) => {
    e.preventDefault();
    if (!selectedRoom || !bedForm.bedNumber) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await roomsService.addBed(selectedRoom.id, bedForm);
      toast.success('Bed added successfully');
      setShowAddBedModal(false);
      resetBedForm();
      loadData();
      onRoomUpdate?.();
    } catch (error) {
      toast.error('Failed to add bed: ' + (error.message || 'Unknown error'));
    }
  };

  const handleUpdateBed = async (bed, updates) => {
    try {
      await roomsService.updateBed(bed.id, updates);
      toast.success('Bed updated successfully');
      loadData();
      onRoomUpdate?.();
    } catch (error) {
      toast.error('Failed to update bed: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDeleteBed = async (bed) => {
    if (!window.confirm(`Are you sure you want to delete Bed ${bed.bedNumber}?`)) {
      return;
    }

    try {
      await roomsService.deleteBed(bed.id);
      toast.success('Bed deleted successfully');
      loadData();
      onRoomUpdate?.();
    } catch (error) {
      toast.error('Failed to delete bed: ' + (error.message || 'Unknown error'));
    }
  };

  const resetRoomForm = () => {
    setRoomForm({
      roomNumber: '',
      roomTypeId: '',
      propertyId: '',
      status: 'AVAILABLE'
    });
  };

  const resetBedForm = () => {
    setBedForm({
      bedNumber: '',
      isAvailable: true,
      isFemaleOnly: false
    });
  };

  const openEditModal = (room) => {
    setSelectedRoom(room);
    setRoomForm({
      roomNumber: room.roomNumber,
      roomTypeId: room.roomTypeId,
      propertyId: room.propertyId,
      status: room.status
    });
    setShowEditRoomModal(true);
  };

  const openAddBedModal = (room) => {
    setSelectedRoom(room);
    setBedForm({
      bedNumber: '',
      isAvailable: true,
      isFemaleOnly: false
    });
    setShowAddBedModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      'AVAILABLE': Colors.available,
      'OCCUPIED': Colors.occupied,
      'CLEANING': Colors.cleaning,
      'BLOCKED': Colors.blocked
    };
    return colors[status] || Colors.muted;
  };

  const getStatusIcon = (status) => {
    const icons = {
      'AVAILABLE': Icons.available,
      'OCCUPIED': Icons.occupied,
      'CLEANING': Icons.cleaning,
      'BLOCKED': Icons.blocked
    };
    return icons[status] || Icons.available;
  };

  const getBedStatusIcon = (bed) => {
    if (!bed.isAvailable) return Icons.close;
    if (bed.isFemaleOnly) return Icons.female;
    return Icons.bed;
  };

  // Helper function to get room image count
  const getRoomImageCount = (roomId) => {
    return roomImages[roomId]?.length || 0;
  };

  // Helper function to get room primary image
  const getRoomPrimaryImage = (roomId) => {
    const images = roomImages[roomId];
    return images && images.length > 0 ? images[0] : null;
  };

  if (loading) {
    return (
      <div style={{
        padding: isMobile ? '12px' : '24px',
        backgroundColor: Colors.light,
        minHeight: '100vh',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          color: Colors.muted
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px', animation: 'spin 1s linear infinite' }}>
            {Icons.loading}
          </div>
          <div style={{ fontSize: '16px', fontWeight: '500' }}>Loading rooms...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: isMobile ? '12px' : '24px',
      backgroundColor: Colors.light,
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      {/* Professional Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: '12px',
        marginBottom: '24px',
        padding: isMobile ? '14px' : '20px',
        backgroundColor: Colors.white,
        borderRadius: '8px',
        border: `1px solid ${Colors.border}`,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
      }}>
        <div>
          <h1 style={{ 
            margin: 0, 
            fontSize: isMobile ? '20px' : '24px', 
            fontWeight: '600', 
            color: Colors.primary,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '20px' }}>{Icons.room}</span>
            Room Management
          </h1>
          <p style={{ 
            margin: isMobile ? '6px 0 0 0' : '4px 0 0 32px', 
            color: Colors.muted,
            fontSize: '14px'
          }}>
            Manage rooms, beds, and accommodation configurations
          </p>
        </div>
        <button
          onClick={() => setShowAddRoomModal(true)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            textDecoration: 'none',
            backgroundColor: Colors.accent,
            color: Colors.white,
            width: isMobile ? '100%' : 'auto',
            justifyContent: 'center'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#163626'}
          onMouseOut={(e) => e.target.style.backgroundColor = Colors.accent}
        >
          <span>{Icons.add}</span>
          Add Room
        </button>
      </div>

      {/* Rooms Grid */}
      {rooms.length === 0 ? (
        <div style={{
          backgroundColor: Colors.white,
          border: `1px solid ${Colors.border}`,
          borderRadius: '8px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          textAlign: 'center',
          padding: '80px 40px',
          marginTop: '24px'
        }}>
          <div style={{ 
            fontSize: '64px', 
            color: Colors.muted, 
            marginBottom: '24px' 
          }}>
            {Icons.room}
          </div>
          <h3 style={{ 
            margin: '0 0 12px 0', 
            color: Colors.secondary,
            fontSize: '20px',
            fontWeight: '600'
          }}>
            No rooms configured
          </h3>
          <p style={{ 
            color: Colors.muted, 
            marginBottom: '24px',
            fontSize: '14px'
          }}>
            Get started by adding your first room to the system
          </p>
          <button
            onClick={() => setShowAddRoomModal(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              textDecoration: 'none',
              backgroundColor: Colors.accent,
              color: Colors.white
            }}
          >
            <span>{Icons.add}</span>
            Add First Room
          </button>
        </div>
      ) : (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))', 
          gap: '20px' 
        }}>
          {rooms.map(room => (
            <div
              key={room.id}
              style={{
                backgroundColor: Colors.white,
                border: `1px solid ${Colors.border}`,
                borderRadius: '8px',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                transition: 'all 0.2s ease-in-out',
                overflow: 'hidden'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
              }}
            >
              {/* Room Header */}
              <div style={{
                padding: '20px',
                borderBottom: `1px solid ${Colors.border}`,
                backgroundColor: Colors.light
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start' 
                }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ 
                      margin: '0 0 8px 0', 
                      fontSize: '18px', 
                      fontWeight: '600',
                      color: Colors.primary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>{Icons.room}</span>
                      Room {room.roomNumber}
                    </h3>
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '4px' 
                    }}>
                      <p style={{ 
                        margin: 0, 
                        color: Colors.muted, 
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span>{room.roomType?.isShared ? Icons.shared : Icons.private}</span>
                        {room.roomType?.name || 'Unknown Type'}
                      </p>
                      <p style={{ 
                        margin: 0, 
                        color: Colors.muted, 
                        fontSize: '13px' 
                      }}>
                        {room.property?.name || 'Unknown Property'}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                    <span style={{
                      backgroundColor: getStatusColor(room.status),
                      color: Colors.white,
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '500',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span>{getStatusIcon(room.status)}</span>
                      {room.status}
                    </span>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: Colors.success,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px'
                    }}>
                      <span>{Icons.currency}</span>
                      {room.roomType?.defaultRate || 0}/night
                    </div>
                  </div>
                </div>
              </div>

              {/* Room Stats */}
              <div style={{
                padding: '16px 20px',
                backgroundColor: Colors.white,
                borderBottom: `1px solid ${Colors.border}`
              }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, 1fr)', 
                  gap: '16px',
                  fontSize: '13px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: Colors.muted
                  }}>
                    <span>{Icons.bed}</span>
                    <span>{room.beds?.length || 0} beds</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: Colors.muted
                  }}>
                    <span>{room.roomType?.isShared ? Icons.shared : Icons.private}</span>
                    <span>{room.roomType?.isShared ? 'Shared' : 'Private'}</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: Colors.success
                  }}>
                    <span>{Icons.available}</span>
                    <span>{room.beds?.filter(bed => bed.isAvailable).length || 0} available</span>
                  </div>
                </div>
              </div>

              {/* Beds Section */}
              <div style={{ padding: '20px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: isMobile ? 'stretch' : 'center', 
                  flexDirection: isMobile ? 'column' : 'row',
                  marginBottom: '16px' 
                }}>
                  <h4 style={{ 
                    margin: 0, 
                    fontSize: '14px', 
                    fontWeight: '600',
                    color: Colors.secondary,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span>{Icons.bed}</span>
                    Bed Configuration
                  </h4>
                  <button
                    onClick={() => openAddBedModal(room)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '6px 12px',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      textDecoration: 'none',
                      backgroundColor: Colors.success,
                      color: Colors.white
                    }}
                  >
                    <span>{Icons.add}</span>
                    Add Bed
                  </button>
                </div>
                
                {room.beds && room.beds.length > 0 ? (
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fill, minmax(120px, 1fr))', 
                    gap: '12px' 
                  }}>
                    {room.beds.map(bed => (
                      <div
                        key={bed.id}
                        style={{
                          border: `1px solid ${Colors.border}`,
                          borderRadius: '6px',
                          padding: '12px',
                          textAlign: 'center',
                          backgroundColor: bed.isAvailable ? '#F0F3EE' : '#F9EEEB',
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        <div style={{ 
                          fontSize: '16px', 
                          marginBottom: '6px',
                          color: bed.isAvailable ? Colors.success : Colors.danger
                        }}>
                          {getBedStatusIcon(bed)}
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          fontWeight: '600', 
                          marginBottom: '4px',
                          color: Colors.primary
                        }}>
                          Bed {bed.bedNumber}
                        </div>
                        <div style={{ 
                          fontSize: '10px', 
                          color: Colors.muted,
                          marginBottom: '8px'
                        }}>
                          {bed.isFemaleOnly && (
                            <div style={{ 
                              color: '#8E3628',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '2px'
                            }}>
                              <span>{Icons.female}</span>
                              Female Only
                            </div>
                          )}
                          {!bed.isAvailable && (
                            <div style={{ 
                              color: Colors.danger,
                              fontWeight: '500'
                            }}>
                              Unavailable
                            </div>
                          )}
                        </div>
                        
                        {/* Bed Actions */}
                        <div style={{ 
                          display: 'flex', 
                          gap: '4px', 
                          justifyContent: 'center',
                          flexWrap: 'wrap'
                        }}>
                          <button
                            onClick={() => handleUpdateBed(bed, { isAvailable: !bed.isAvailable })}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '28px',
                              height: '28px',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease-in-out',
                              backgroundColor: bed.isAvailable ? Colors.danger : Colors.success,
                              color: Colors.white,
                              position: 'relative'
                            }}
                            title={bed.isAvailable ? 'Mark as Unavailable' : 'Mark as Available'}
                            onMouseOver={(e) => {
                              e.target.style.transform = 'scale(1.1)';
                              e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.transform = 'scale(1)';
                              e.target.style.boxShadow = 'none';
                            }}
                          >
                            {bed.isAvailable ? Icons.power : Icons.check}
                          </button>
                          
                          <button
                            onClick={() => handleUpdateBed(bed, { isFemaleOnly: !bed.isFemaleOnly })}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '28px',
                              height: '28px',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease-in-out',
                              backgroundColor: bed.isFemaleOnly ? '#8E3628' : Colors.muted,
                              color: Colors.white,
                              position: 'relative'
                            }}
                            title={bed.isFemaleOnly ? 'Remove Female Only Restriction' : 'Set as Female Only'}
                            onMouseOver={(e) => {
                              e.target.style.transform = 'scale(1.1)';
                              e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.transform = 'scale(1)';
                              e.target.style.boxShadow = 'none';
                            }}
                          >
                            {Icons.female}
                          </button>
                          
                          <button
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to delete Bed ${bed.bedNumber}? This action cannot be undone.`)) {
                                handleDeleteBed(bed);
                              }
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '28px',
                              height: '28px',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease-in-out',
                              backgroundColor: Colors.danger,
                              color: Colors.white,
                              position: 'relative'
                            }}
                            title="Delete Bed"
                            onMouseOver={(e) => {
                              e.target.style.transform = 'scale(1.1)';
                              e.target.style.backgroundColor = '#772D21';
                              e.target.style.boxShadow = '0 2px 8px rgba(142, 54, 40, 0.3)';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.transform = 'scale(1)';
                              e.target.style.backgroundColor = Colors.danger;
                              e.target.style.boxShadow = 'none';
                            }}
                          >
                            {Icons.delete}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '24px', 
                    color: Colors.muted, 
                    fontSize: '13px',
                    backgroundColor: Colors.light,
                    borderRadius: '6px',
                    border: `1px dashed ${Colors.border}`
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>{Icons.bed}</div>
                    No beds configured yet
                  </div>
                )}
              </div>

              {/* Room Actions */}
              <div style={{
                padding: '16px 20px',
                backgroundColor: Colors.light,
                borderTop: `1px solid ${Colors.border}`,
                display: 'flex',
                gap: '8px',
                justifyContent: 'flex-end',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => openEditModal(room)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '8px 12px',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: Colors.warning,
                    color: Colors.white
                  }}
                >
                  <span>{Icons.edit}</span>
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteRoom(room)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '8px 12px',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: Colors.danger,
                    color: Colors.white
                  }}
                >
                  <span>{Icons.delete}</span>
                  Delete
                </button>
                <button
                  onClick={() => openImageModal(room)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '8px 12px',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: Colors.accent,
                    color: Colors.white
                  }}
                >
                  <span>{Icons.image}</span>
                  Manage Images
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Room Modal */}
      {showAddRoomModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: Colors.white,
            borderRadius: '8px',
            width: '500px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            border: `1px solid ${Colors.border}`,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: `1px solid ${Colors.border}`,
              backgroundColor: Colors.light
            }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: '20px', 
                fontWeight: '600',
                color: Colors.primary,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>{Icons.add}</span>
                Add New Room
              </h3>
            </div>
            
            <form onSubmit={handleCreateRoom} style={{ padding: '24px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '500',
                  color: Colors.secondary,
                  fontSize: '14px'
                }}>
                  Room Number *
                </label>
                <input
                  type="text"
                  value={roomForm.roomNumber}
                  onChange={(e) => setRoomForm({ ...roomForm, roomNumber: e.target.value })}
                  placeholder="e.g., 101, A-1, etc."
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${Colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease-in-out',
                    ':focus': {
                      outline: 'none',
                      borderColor: Colors.accent,
                      boxShadow: `0 0 0 3px rgba(26, 62, 42, 0.1)`
                    }
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '500',
                  color: Colors.secondary,
                  fontSize: '14px'
                }}>
                  Property *
                </label>
                <select
                  value={roomForm.propertyId}
                  onChange={(e) => setRoomForm({ ...roomForm, propertyId: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${Colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease-in-out',
                    ':focus': {
                      outline: 'none',
                      borderColor: Colors.accent,
                      boxShadow: `0 0 0 3px rgba(26, 62, 42, 0.1)`
                    }
                  }}
                  required
                >
                  <option value="">Select Property</option>
                  {properties.map(property => (
                    <option key={property.id} value={property.id}>
                      {property.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '500',
                  color: Colors.secondary,
                  fontSize: '14px'
                }}>
                  Room Type *
                </label>
                <select
                  value={roomForm.roomTypeId}
                  onChange={(e) => setRoomForm({ ...roomForm, roomTypeId: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${Colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease-in-out',
                    ':focus': {
                      outline: 'none',
                      borderColor: Colors.accent,
                      boxShadow: `0 0 0 3px rgba(26, 62, 42, 0.1)`
                    }
                  }}
                  required
                >
                  <option value="">Select Room Type</option>
                  {roomTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name} - ${type.defaultRate}/night ({type.isShared ? 'Shared' : 'Private'})
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '500',
                  color: Colors.secondary,
                  fontSize: '14px'
                }}>
                  Status
                </label>
                <select
                  value={roomForm.status}
                  onChange={(e) => setRoomForm({ ...roomForm, status: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${Colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease-in-out',
                    ':focus': {
                      outline: 'none',
                      borderColor: Colors.accent,
                      boxShadow: `0 0 0 3px rgba(26, 62, 42, 0.1)`
                    }
                  }}
                >
                  <option value="AVAILABLE">Available</option>
                  <option value="OCCUPIED">Occupied</option>
                  <option value="CLEANING">Cleaning</option>
                  <option value="BLOCKED">Blocked</option>
                </select>
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                justifyContent: 'flex-end',
                paddingTop: '16px',
                borderTop: `1px solid ${Colors.border}`
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddRoomModal(false);
                    resetRoomForm();
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '10px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: Colors.muted,
                    color: Colors.white
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '10px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: Colors.accent,
                    color: Colors.white
                  }}
                >
                  <span>{Icons.add}</span>
                  Create Room
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Room Modal */}
      {showEditRoomModal && selectedRoom && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: Colors.white,
            borderRadius: '8px',
            width: '500px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            border: `1px solid ${Colors.border}`,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: `1px solid ${Colors.border}`,
              backgroundColor: Colors.light
            }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: '20px', 
                fontWeight: '600',
                color: Colors.primary,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>{Icons.edit}</span>
                Edit Room {selectedRoom.roomNumber}
              </h3>
            </div>
            
            <form onSubmit={handleUpdateRoom} style={{ padding: '24px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '500',
                  color: Colors.secondary,
                  fontSize: '14px'
                }}>
                  Room Number *
                </label>
                <input
                  type="text"
                  value={roomForm.roomNumber}
                  onChange={(e) => setRoomForm({ ...roomForm, roomNumber: e.target.value })}
                  placeholder="e.g., 101, A-1, etc."
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${Colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease-in-out',
                    ':focus': {
                      outline: 'none',
                      borderColor: Colors.accent,
                      boxShadow: `0 0 0 3px rgba(26, 62, 42, 0.1)`
                    }
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '500',
                  color: Colors.secondary,
                  fontSize: '14px'
                }}>
                  Property *
                </label>
                <select
                  value={roomForm.propertyId}
                  onChange={(e) => setRoomForm({ ...roomForm, propertyId: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${Colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease-in-out',
                    ':focus': {
                      outline: 'none',
                      borderColor: Colors.accent,
                      boxShadow: `0 0 0 3px rgba(26, 62, 42, 0.1)`
                    }
                  }}
                  required
                >
                  <option value="">Select Property</option>
                  {properties.map(property => (
                    <option key={property.id} value={property.id}>
                      {property.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '500',
                  color: Colors.secondary,
                  fontSize: '14px'
                }}>
                  Room Type *
                </label>
                <select
                  value={roomForm.roomTypeId}
                  onChange={(e) => setRoomForm({ ...roomForm, roomTypeId: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${Colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease-in-out',
                    ':focus': {
                      outline: 'none',
                      borderColor: Colors.accent,
                      boxShadow: `0 0 0 3px rgba(26, 62, 42, 0.1)`
                    }
                  }}
                  required
                >
                  <option value="">Select Room Type</option>
                  {roomTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name} - ${type.defaultRate}/night ({type.isShared ? 'Shared' : 'Private'})
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '500',
                  color: Colors.secondary,
                  fontSize: '14px'
                }}>
                  Status
                </label>
                <select
                  value={roomForm.status}
                  onChange={(e) => setRoomForm({ ...roomForm, status: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${Colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease-in-out',
                    ':focus': {
                      outline: 'none',
                      borderColor: Colors.accent,
                      boxShadow: `0 0 0 3px rgba(26, 62, 42, 0.1)`
                    }
                  }}
                >
                  <option value="AVAILABLE">Available</option>
                  <option value="OCCUPIED">Occupied</option>
                  <option value="CLEANING">Cleaning</option>
                  <option value="BLOCKED">Blocked</option>
                </select>
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                justifyContent: 'flex-end',
                paddingTop: '16px',
                borderTop: `1px solid ${Colors.border}`
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditRoomModal(false);
                    setSelectedRoom(null);
                    resetRoomForm();
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '10px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: Colors.muted,
                    color: Colors.white
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '10px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: Colors.warning,
                    color: Colors.white
                  }}
                >
                  <span>{Icons.edit}</span>
                  Update Room
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Bed Modal */}
      {showAddBedModal && selectedRoom && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: Colors.white,
            borderRadius: '8px',
            width: '500px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            border: `1px solid ${Colors.border}`,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: `1px solid ${Colors.border}`,
              backgroundColor: Colors.light
            }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: '20px', 
                fontWeight: '600',
                color: Colors.primary,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>{Icons.add}</span>
                Add Bed to Room {selectedRoom.roomNumber}
              </h3>
            </div>
            
            <form onSubmit={handleAddBed} style={{ padding: '24px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '500',
                  color: Colors.secondary,
                  fontSize: '14px'
                }}>
                  Bed Number *
                </label>
                <input
                  type="text"
                  value={bedForm.bedNumber}
                  onChange={(e) => setBedForm({ ...bedForm, bedNumber: e.target.value })}
                  placeholder="e.g., 1, 2, A, B, etc."
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${Colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease-in-out',
                    ':focus': {
                      outline: 'none',
                      borderColor: Colors.accent,
                      boxShadow: `0 0 0 3px rgba(26, 62, 42, 0.1)`
                    }
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px' 
                }}>
                  <input
                    type="checkbox"
                    checked={bedForm.isAvailable}
                    onChange={(e) => setBedForm({ ...bedForm, isAvailable: e.target.checked })}
                    style={{
                      accentColor: Colors.accent
                    }}
                  />
                  <span style={{ fontWeight: '500' }}>Available for booking</span>
                </label>
                <small style={{ color: Colors.muted, marginLeft: '28px' }}>
                  Uncheck if bed is under maintenance or temporarily unavailable
                </small>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px' 
                }}>
                  <input
                    type="checkbox"
                    checked={bedForm.isFemaleOnly}
                    onChange={(e) => setBedForm({ ...bedForm, isFemaleOnly: e.target.checked })}
                    style={{
                      accentColor: '#8E3628'
                    }}
                  />
                  <span style={{ fontWeight: '500', color: '#8E3628' }}>Female Only</span>
                </label>
                <small style={{ color: Colors.muted, marginLeft: '28px' }}>
                  Check if this bed is reserved exclusively for female guests
                </small>
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                justifyContent: 'flex-end',
                paddingTop: '16px',
                borderTop: `1px solid ${Colors.border}`
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddBedModal(false);
                    setSelectedRoom(null);
                    resetBedForm();
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '10px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: Colors.muted,
                    color: Colors.white
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '10px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: Colors.success,
                    color: Colors.white
                  }}
                >
                  <span>{Icons.add}</span>
                  Add Bed
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Management Modal */}
      {showImageModal && selectedRoom && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: Colors.white,
            borderRadius: '8px',
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            border: `1px solid ${Colors.border}`,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: `1px solid ${Colors.border}`,
              backgroundColor: Colors.light
            }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: '20px', 
                fontWeight: '600',
                color: Colors.primary,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>{Icons.image}</span>
                Manage Room Images
              </h3>
            </div>

            <div style={{ padding: '24px' }}>
              {/* Room Image Upload Section */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ 
                  margin: '0 0 16px 0', 
                  fontSize: '16px', 
                  fontWeight: '500',
                  color: Colors.secondary
                }}>
                  Upload Room Images
                </h4>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  multiple
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${Colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    marginBottom: '12px',
                    cursor: 'pointer'
                  }}
                />
                {imagePreview && (
                  <div style={{
                    marginBottom: '12px',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: '6px',
                        objectFit: 'cover'
                      }}
                    />
                    <button
                      onClick={() => setImagePreview(null)}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <span style={{ color: Colors.primary }}>{Icons.close}</span>
                      Remove
                    </button>
                  </div>
                )}
                <button
                  onClick={handleImageUpload}
                  disabled={uploadingImages}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '10px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: uploadingImages ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: uploadingImages ? Colors.muted : Colors.success,
                    color: Colors.white,
                    width: '100%'
                  }}
                >
                  {uploadingImages ? (
                    <>
                      <span className="loader"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <span>{Icons.upload}</span>
                      Upload Images
                    </>
                  )}
                </button>
              </div>

              {/* Existing Room Images Section */}
              <div>
                <h4 style={{ 
                  margin: '0 0 16px 0', 
                  fontSize: '16px', 
                  fontWeight: '500',
                  color: Colors.secondary
                }}>
                  Existing Room Images ({getRoomImageCount(selectedRoom.id)} total)
                </h4>
                {roomImages[selectedRoom.id]?.length > 0 ? (
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', 
                    gap: '12px' 
                  }}>
                    {roomImages[selectedRoom.id].map(image => (
                      <div
                        key={image.id}
                        style={{
                          border: `1px solid ${Colors.border}`,
                          borderRadius: '6px',
                          overflow: 'hidden',
                          position: 'relative',
                          cursor: 'pointer',
                          transition: 'transform 0.2s ease-in-out'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        onClick={() => handleImageClick(image)}
                      >
                        <img
                          src={image.url}
                          alt={image.description || 'Room Image'}
                          style={{
                            width: '100%',
                            height: 'auto',
                            objectFit: 'cover',
                            borderRadius: '6px',
                            display: 'block'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div style={{
                          display: 'none',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '120px',
                          backgroundColor: '#EEEBE1',
                          color: '#5F6B61',
                          fontSize: '12px',
                          textAlign: 'center'
                        }}>
                          <div>
                            <div><ImageIcon size="1em" aria-hidden="true" /></div>
                            <div>Image not available</div>
                          </div>
                        </div>
                        
                        {/* Image overlay with click hint */}
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '500'
                        }}>
                          Click to view
                        </div>
                        <div style={{
                          padding: '8px',
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          borderTop: `1px solid ${Colors.border}`,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px'
                        }}>
                          <div style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            color: Colors.primary
                          }}>
                            Image {image.id}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: Colors.muted,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <span>{Icons.camera}</span>
                            Uploaded on {new Date(image.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteImage(image.id, selectedRoom.id);
                          }}
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <span style={{ color: Colors.danger }}>{Icons.delete}</span>
                          Delete
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const newDescription = prompt('Enter new description for the image:', image.description);
                            if (newDescription !== null) {
                              handleUpdateImageDescription(image.id, newDescription, selectedRoom.id);
                            }
                          }}
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '80px',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <span style={{ color: Colors.primary }}>{Icons.edit}</span>
                          Edit Description
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '24px', 
                    color: Colors.muted, 
                    fontSize: '13px',
                    backgroundColor: Colors.light,
                    borderRadius: '6px',
                    border: `1px dashed ${Colors.border}`
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>{Icons.image}</div>
                    No images uploaded for this room yet
                  </div>
                )}
              </div>
            </div>

            <div style={{
              padding: '16px',
              borderTop: `1px solid ${Colors.border}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px'
            }}>
              <button
                onClick={() => {
                  setShowImageModal(false);
                  setSelectedRoom(null);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '10px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  backgroundColor: Colors.muted,
                  color: Colors.white
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Large Image Viewer Modal */}
      {selectedImageForView && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          padding: '20px'
        }}>
          <div style={{
            position: 'relative',
            maxWidth: '90%',
            maxHeight: '90%'
          }}>
            {/* Close button */}
            <button
              onClick={handleImageViewClose}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: 'white',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '6px',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.3)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              }}
            >
              ✕
            </button>
            
            {/* Image */}
            <img
              src={selectedImageForView.url}
              alt={selectedImageForView.description || 'Room Image'}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                borderRadius: '8px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            
            {/* Error fallback */}
            <div style={{
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '300px',
              minWidth: '400px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              color: 'white',
              borderRadius: '8px',
              textAlign: 'center',
              fontSize: '16px'
            }}>
              <div>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}><ImageIcon size="1em" aria-hidden="true" /></div>
                <div style={{ marginBottom: '8px' }}>Image not available</div>
                <div style={{ fontSize: '14px', opacity: 0.8 }}>
                  The image could not be loaded
                </div>
              </div>
            </div>
            
            {/* Image info */}
            <div style={{
              position: 'absolute',
              bottom: '-60px',
              left: '0',
              right: '0',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              color: 'white',
              padding: '12px 16px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                Room Image #{selectedImageForView.id}
              </div>
              {selectedImageForView.description && (
                <div style={{ fontSize: '14px', opacity: 0.9 }}>
                  {selectedImageForView.description}
                </div>
              )}
              <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '4px' }}>
                Uploaded {new Date(selectedImageForView.createdAt).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoomManagement;